import React, { useMemo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Typography, Box, Modal } from '@mui/material';
import {
  SwitchStyled,
  HenryOxfordIcon,
  SelectionBoxStyled,
  SelectionTitleStyled,
  BoxStyled
} from './style';
import { useAppSelector } from '../../../store/hooks';
import { setCalfCircum } from '../../../store/slices/metaDataSlice';
import { metaDataSelector } from '../../../store/globalSelectors';
import { PlaceholderGrid } from '../../../styles/globalStyles';
import Line from '../../../shared/components/line/index';
import {
  ToggleButtonStyled,
  ToggleButtonGroupStyled
} from '../../../shared/components/toggleButtons/styles';
import { SingleNumberCard } from '../../../shared/components/numberInput/numberCards/singleNumberCard';
import { glimConstants } from '../../../shared/shared.constants';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { AddOnInfoModal } from '../../../shared/components/modal/addOnInfoModal';
import { TextWithInfoIcon } from '../../../shared/components/textWithInfoIcon';
import calfTape from '../../../assets/icons/calfTape.svg';
import InfoIcon from '../../../assets/icons/infoIcon.svg';

export const CalfCircumference = () => {
  const dispatch = useDispatch();
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const { muscleScreening, patientInformation } =
    useAppSelector(metaDataSelector);
  const { calfCircumference } = muscleScreening;

  const handleChangeHealthStatus = (event) => {
    dispatch(
      setCalfCircum({
        ...calfCircumference,
        isPatientHealthy: event.target.value
      })
    );
  };

  const calculatedCircum = useMemo(() => {
    if (calfCircumference.calfCircumferenceVal === 0) return 0;
    let calculatedCircum = 0;
    if (calfCircumference.isPatientHealthy === 'one') {
      glimConstants.calfCalc.healthy.forEach((item) => {
        if (
          patientInformation.bmi >= item[0] &&
          patientInformation.bmi <= item[1]
        ) {
          calculatedCircum =
            Number(calfCircumference.calfCircumferenceVal) + item[2];
        }
      });
    } else {
      glimConstants.calfCalc.nothealthy.forEach((item) => {
        if (
          patientInformation.bmi >= item[0] &&
          patientInformation.bmi <= item[1]
        ) {
          calculatedCircum =
            Number(calfCircumference.calfCircumferenceVal) + item[2];
        }
      });
    }
    if (calfCircumference.isPatientHasOedema) {
      calculatedCircum = calculatedCircum - 2;
    }
    return calculatedCircum;
  }, [
    calfCircumference.calfCircumferenceVal,
    calfCircumference.isPatientHealthy,
    patientInformation.bmi,
    calfCircumference.isPatientHasOedema
  ]);

  useEffect(() => {
    dispatch(
      setCalfCircum({
        ...calfCircumference,
        calculatedCircumference: calculatedCircum
      })
    );
  }, [calculatedCircum]);

  return (
    <>
      <BoxStyled
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <TextWithInfoIcon
          data-testid={'ccInfoPopupButton'}
          title={'CALF CIRCUMFERENCE'}
          icon={InfoIcon}
          fontSize={'1.5rem'}
          lineHeight={'1.375rem'}
          variant={'h1'}
          handleIconClick={() => setOpenInfoModal(true)}
        />
        <HenryOxfordIcon>
          <img src={calfTape} alt="bmimeasure" />
        </HenryOxfordIcon>
        <PlaceholderGrid
          container
          width={'100%'}
          sx={{ display: 'flex', gap: '1.875rem', flexDirection: 'column' }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '1.125rem',
                lineHeight: '1.5rem',
                textAlign: 'center',
                paddingTop: '1.25rem',
                textTransform: 'uppercase',
                color: '#002A3A',
                paddingBottom: '1.25rem',
                fontFamily: 'BGBold'
              }}
            >
              Calf circumference
            </Typography>
            <Box
              sx={{
                border: '0.0625rem solid #D9D9D6',
                borderRadius: '0.9375rem',
                width: '100%',
                textAlign: 'center'
              }}
              data-testid="calf-circumference-input"
            >
              <SingleNumberCard
                width={'100%'}
                title={'ENTER CALF CIRCUMFERENCE'}
                marginVal={'0.625rem'}
                range={{
                  min: 0,
                  max: 70
                }}
                subHead={'(cm)'}
                value={calfCircumference.calfCircumferenceVal}
                setValue={(val: number) => {
                  dispatch(
                    setCalfCircum({
                      ...calfCircumference,
                      calfCircumferenceVal: val
                    })
                  );
                }}
              />
            </Box>
          </Box>
          <Line color="#D9D9D6" height="0.0625rem" />
          <Box>
            <Box sx={{ width: '100%', paddingBottom: '0.625rem' }}>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.25rem',
                  textAlign: 'left',
                  fontFamily: 'Calibri'
                }}
              >
                Choose which best fits your patient:
              </Typography>
            </Box>
            <ToggleButtonGroupStyled
              color="primary"
              value={calfCircumference.isPatientHealthy}
              exclusive
              onChange={handleChangeHealthStatus}
              aria-label="Platform"
              sx={{ padding: '0px', justifyContent: 'space-between' }}
            >
              <ToggleButtonStyled
                sx={{
                  width: '45%',
                  padding: '0.625rem 0.3125rem',
                  lineHeight: '1.25rem',
                  fontWeight: 400,
                  fontSize: '1rem',
                  fontFamily: 'Calibri'
                }}
                value="one"
                data-testid="healthy"
              >
                Healthy
              </ToggleButtonStyled>
              <ToggleButtonStyled
                sx={{
                  width: '45%',
                  padding: '0.625rem 0.3125rem',
                  lineHeight: '1.25rem',
                  fontWeight: 400,
                  fontSize: '1rem',
                  fontFamily: 'Calibri'
                }}
                value="two"
                data-testid="notHealthy"
              >
                Aging/Clinical population with probable muscle or weight loss
              </ToggleButtonStyled>
            </ToggleButtonGroupStyled>
          </Box>
          <Line color="#D9D9D6" height="0.0625rem" />
          <SelectionBoxStyled>
            <SelectionTitleStyled variant="h4">
              Does patient have lower limb oedema?
            </SelectionTitleStyled>
            <SwitchStyled
              checked={calfCircumference.isPatientHasOedema}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(
                  setCalfCircum({
                    ...calfCircumference,
                    isPatientHasOedema: event.target.checked
                  })
                )
              }
              inputProps={{ 'aria-label': 'Switch-Toggle', role: 'checkbox' }}
              data-testid="isPatientHasOedemaSwitch"
            />
          </SelectionBoxStyled>
        </PlaceholderGrid>
      </BoxStyled>
      <NavigationButtons
        nextDisabled={false}
        nextRoute={'/muscle-screening/calf-circumference/screening-result'}
      />
      <Modal
        open={openInfoModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={` `}
          description1={`Calf Circumference should be taken while participant is in seating position`}
          title={`Reference:`}
          content={`Gonzalez CM et al. Am J Clin Nutri. 2021;113:1679-1687
Prado C et al. Clin Nutri. 2022;41:2244-2263.
Ishida Y et al. Geriatr. Gerontol. Int. 2019;19:993-998.`}
          onClose={() => setOpenInfoModal(false)}
        />
      </Modal>
    </>
  );
};
